import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Loader2, Package, Upload, X, Store, ArrowLeft, FileSpreadsheet, Search } from "lucide-react";
import { api, formatApiErrorDetail, productImageUrl, formatMoney, effectivePrice, PRODUCT_UNITS } from "@/lib/api";
import ImportProductsModal from "@/components/admin/ImportProductsModal";
import ProductImage from "@/components/ProductImage";
import useProgressiveList from "@/hooks/useProgressiveList";

const EMPTY = {
  name: "", brand: "", image_path: null, pack_size: "",
  mrp: "", price: "", offer_price: "", offer_description: "", in_stock: true,
  rates: {}, mrps: {}, case_quantity: "",
};

export default function AdminProducts({ brandFilter = null, onClearFilter }) {
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [brandChoice, setBrandChoice] = useState(brandFilter || "");

  useEffect(() => { setBrandChoice(brandFilter || ""); }, [brandFilter]);

  const changeBrand = (val) => {
    setBrandChoice(val);
    if (!val && brandFilter && onClearFilter) onClearFilter();
  };

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/products"), api.get("/brands")])
      .then(([p, b]) => { setProducts(p.data); setBrands(b.data); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm({ ...EMPTY, brand: brandChoice || brands[0]?.name || "", rates: {}, mrps: {} });
    setEditingId(null);
    setError("");
    setShowForm(true);
  };

  const openEdit = (p) => {
    const rates = {};
    (p.unit_rates || []).forEach((ur) => { if (ur && ur.unit != null) rates[ur.unit] = String(ur.rate); });
    const mrps = {};
    (p.unit_mrps || []).forEach((um) => { if (um && um.unit != null) mrps[um.unit] = String(um.mrp); });
    setForm({
      name: p.name, brand: p.brand, image_path: p.image_path,
      pack_size: p.pack_size ?? "", mrp: p.mrp ?? "", price: p.price ?? "",
      offer_price: p.offer_price ?? "", offer_description: p.offer_description ?? "",
      in_stock: p.in_stock, rates, mrps, case_quantity: p.case_quantity ?? "",
    });
    setEditingId(p.id);
    setError("");
    setShowForm(true);
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setForm((f) => ({ ...f, image_path: data.path }));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const num = (v) => (v === "" || v === null ? null : Number(v));
  const setRate = (unit, val) => setForm((f) => ({ ...f, rates: { ...f.rates, [unit]: val } }));
  const setMrp = (unit, val) => setForm((f) => ({ ...f, mrps: { ...f.mrps, [unit]: val } }));

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    const unit_rates = Object.entries(form.rates)
      .filter(([, r]) => r !== "" && r !== null && !Number.isNaN(Number(r)))
      .map(([unit, r]) => ({ unit, rate: Number(r) }));
    const unit_mrps = Object.entries(form.mrps)
      .filter(([, m]) => m !== "" && m !== null && !Number.isNaN(Number(m)))
      .map(([unit, m]) => ({ unit, mrp: Number(m) }));
    const payload = {
      name: form.name,
      brand: form.brand,
      image_path: form.image_path,
      pack_size: form.pack_size || null,
      mrp: num(form.mrp),
      price: num(form.price),
      offer_price: num(form.offer_price),
      offer_description: form.offer_description || null,
      in_stock: !!form.in_stock,
      unit_rates,
      unit_mrps,
      case_quantity: form.case_quantity === "" ? null : Number(form.case_quantity),
    };
    try {
      if (editingId) await api.put(`/products/${editingId}`, payload);
      else await api.post("/products", payload);
      setShowForm(false);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    load();
  };

  const field = "w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100";
  const labelCls = "mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500";

  const q = search.trim().toLowerCase();
  const shown = products.filter((p) =>
    (!brandChoice || p.brand === brandChoice) &&
    (!q || (p.name || "").toLowerCase().includes(q))
  );
  const { visible, sentinelRef, hasMore } = useProgressiveList(shown, 40, `${brandChoice}|${q}`);

  return (
    <div data-testid="admin-products">
      <div className="mb-4 flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative flex-1 sm:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="product-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search product name..."
                className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-9 pr-8 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
              {search && (
                <button
                  data-testid="clear-product-search"
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <select
              data-testid="product-brand-filter"
              value={brandChoice}
              onChange={(e) => changeBrand(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 sm:w-52"
            >
              <option value="">All Brands</option>
              {brands.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
            </select>
            {brandChoice && (
              <button
                data-testid="clear-brand-filter"
                onClick={() => changeBrand("")}
                className="flex shrink-0 items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> All Products
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="import-products-button" onClick={() => setImportOpen(true)} className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">
              <FileSpreadsheet className="h-4 w-4" /> Import Products
            </button>
            <button data-testid="admin-new-product-button" onClick={openNew} className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700">
              <Plus className="h-4 w-4" /> {brandChoice ? "Add Product" : "New Product"}
            </button>
          </div>
        </div>
        <p data-testid="product-result-count" className="text-sm text-slate-500">
          {shown.length} product{shown.length === 1 ? "" : "s"}
          {brandChoice ? <> in <span className="font-semibold text-slate-700">{brandChoice}</span></> : null}
          {q ? <> matching “<span className="font-semibold text-slate-700">{search.trim()}</span>”</> : null}
        </p>
      </div>

      {importOpen && <ImportProductsModal onClose={() => setImportOpen(false)} onImported={load} />}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
      ) : shown.length === 0 ? (
        <p className="py-12 text-center text-sm text-slate-400">
          {q
            ? `No products match “${search.trim()}”${brandChoice ? ` in ${brandChoice}` : ""}.`
            : brandChoice
              ? `No products for ${brandChoice} yet. Add the first one.`
              : "No products yet. Add your first product."}
        </p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-100 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Product</th>
                <th className="hidden px-4 py-3 sm:table-cell">Brand</th>
                <th className="hidden px-4 py-3 text-right md:table-cell">MRP</th>
                <th className="px-4 py-3 text-right">Price</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => (
                <tr key={p.id} data-testid={`admin-product-${p.id}`} className="border-t border-slate-100">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-3">
                      <ProductImage path={p.image_path} alt={p.name} iconClass="h-6 w-6" className="h-14 w-14 rounded-lg border border-slate-100" testId={`admin-product-image-${p.id}`} />
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800">
                          {p.name}
                          {p.pack_size ? <span className="text-slate-400"> · {p.pack_size}</span> : null}
                        </p>
                        <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[11px]">
                          <span className="text-slate-400 sm:hidden">{p.brand}</span>
                          {p.has_offer && <span className="font-semibold text-amber-600">Offer</span>}
                          {!p.in_stock && <span className="font-semibold text-red-500">Out of stock</span>}
                          {(p.unit_rates || []).length > 0 && (
                            <span className="text-slate-400">{p.unit_rates.map((u) => u.unit).join(", ")}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="hidden px-4 py-2.5 text-slate-500 sm:table-cell">{p.brand}</td>
                  <td className="hidden px-4 py-2.5 text-right text-slate-400 md:table-cell">{formatMoney(p.mrp) || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-semibold text-blue-700">{formatMoney(effectivePrice(p)) || "—"}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end gap-2">
                      <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-slate-500 hover:border-blue-300 hover:text-blue-600" aria-label="Edit product"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`delete-product-${p.id}`} onClick={() => remove(p.id)} className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-500" aria-label="Delete product"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {hasMore && <div ref={sentinelRef} data-testid="admin-products-load-more" className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-blue-400" /></div>}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setShowForm(false)} />
          <form onSubmit={save} data-testid="product-form" className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-800">{editingId ? "Edit Product" : "New Product"}</h3>
              <button type="button" onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className={labelCls}>Brand</label>
                <select data-testid="product-brand-select" required value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} disabled={!!brandFilter} className={`${field} ${brandFilter ? "cursor-not-allowed bg-slate-100 text-slate-600" : ""}`}>
                  <option value="" disabled>Select brand</option>
                  {brands.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
                </select>
                {brandFilter && <p className="mt-1 text-[11px] text-slate-400">Brand is locked to {brandFilter} for this view.</p>}
              </div>

              <div>
                <label className={labelCls}>Product Name</label>
                <input data-testid="product-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Sunflower Oil" className={field} />
              </div>

              <div>
                <label className={labelCls}>Product Image</label>
                <div className="flex items-center gap-3 rounded-lg border border-dashed border-slate-300 p-3">
                  <ProductImage path={form.image_path} iconClass="h-6 w-6" className="h-16 w-16 rounded border border-slate-100" testId="product-form-image-preview" />
                  <label className="flex cursor-pointer items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {uploading ? "Uploading..." : form.image_path ? "Change image" : "Upload image"}
                    <input data-testid="product-image-input" type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
                  </label>
                </div>
              </div>

              <div>
                <label className={labelCls}>Pack Size / Weight</label>
                <input data-testid="product-packsize-input" value={form.pack_size} onChange={(e) => setForm({ ...form, pack_size: e.target.value })} placeholder="e.g. 1 L, 500 g, 1 kg" className={field} />
              </div>

              <div className="rounded-lg bg-slate-50 p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Pricing (all optional)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>MRP <span className="font-normal text-slate-400">(optional)</span></label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                      <input data-testid="product-mrp-input" type="number" step="0.01" inputMode="decimal" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} placeholder="0.00" className={field.replace("px-3", "pl-7 pr-3")} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Selling Price <span className="font-normal text-slate-400">(optional)</span></label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                      <input data-testid="product-price-input" type="number" step="0.01" inputMode="decimal" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="0.00" className={field.replace("px-3", "pl-7 pr-3")} />
                    </div>
                  </div>
                </div>
                <p className="mt-2 text-[11px] text-slate-400">Tip: You can skip these and use the Unit Rates below.</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Offer Price <span className="font-normal text-slate-400">(optional)</span></label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                    <input data-testid="product-offer-price-input" type="number" step="0.01" inputMode="decimal" value={form.offer_price} onChange={(e) => setForm({ ...form, offer_price: e.target.value })} placeholder="0.00" className={field.replace("px-3", "pl-7 pr-3")} />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Stock Status</label>
                  <select data-testid="product-stock-select" value={form.in_stock ? "in" : "out"} onChange={(e) => setForm({ ...form, in_stock: e.target.value === "in" })} className={field}>
                    <option value="in">In Stock</option>
                    <option value="out">Out of Stock</option>
                  </select>
                </div>
              </div>

              <div>
                <label className={labelCls}>Offer Description</label>
                <input data-testid="product-offer-desc-input" value={form.offer_description} onChange={(e) => setForm({ ...form, offer_description: e.target.value })} placeholder="e.g. 10% OFF this week" className={field} />
              </div>

              {/* Unit rates */}
              <div className="rounded-xl border-2 border-blue-100 bg-blue-50/40 p-4" data-testid="unit-rates-section">
                <p className="text-sm font-bold text-slate-800">Unit Rates &amp; MRP</p>
                <p className="mb-3 text-xs text-slate-500">Fill only the units that apply. Enter each unit's own MRP so the customer sees the correct MRP for Piece vs Case. Leave the rest blank.</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                  {PRODUCT_UNITS.map((u) => (
                    <div key={u} className="rounded-lg border border-blue-100 bg-white p-2">
                      <label className="mb-1.5 block text-xs font-bold text-slate-700">{u}</label>
                      <div className="space-y-1.5">
                        <div className="relative">
                          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-400">₹</span>
                          <input
                            data-testid={`unit-rate-${u}`}
                            type="number"
                            step="0.01"
                            inputMode="decimal"
                            value={form.rates[u] ?? ""}
                            onChange={(e) => setRate(u, e.target.value)}
                            placeholder="Rate"
                            className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-7 pr-2 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                          />
                        </div>
                        <div className="relative">
                          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-400">₹</span>
                          <input
                            data-testid={`unit-mrp-${u}`}
                            type="number"
                            step="0.01"
                            inputMode="decimal"
                            value={form.mrps[u] ?? ""}
                            onChange={(e) => setMrp(u, e.target.value)}
                            placeholder="MRP (optional)"
                            className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-7 pr-2 text-xs text-slate-600 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4">
                  <label className="mb-1 block text-xs font-semibold text-slate-600">Pieces per Case</label>
                  <input
                    data-testid="product-case-quantity"
                    type="number"
                    inputMode="numeric"
                    min="0"
                    value={form.case_quantity}
                    onChange={(e) => setForm({ ...form, case_quantity: e.target.value })}
                    placeholder="e.g. 100 (shown as '1 Case = 100 pcs')"
                    className="w-full rounded-lg border border-slate-200 bg-white py-3 px-3 text-base outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                  <p className="mt-1 text-[11px] text-slate-400">Enter the quantity yourself. It is not calculated from the rates.</p>
                </div>
              </div>
            </div>

            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

            <div className="mt-5 flex gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="flex-1 rounded-lg border border-slate-200 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button data-testid="save-product-button" type="submit" disabled={saving} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-70">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Product"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
